import { initContract } from "@ts-rest/core";
import { z } from "zod";
import { ErrorMessageSchema } from "@common/schemas/error.schema";
import {
    OrganizationPathParamsSchema,
    WorkspacePathParamsSchema,
} from "../schemas/organization-service.schema";
import {
    TaskTemplateCreateSchema,
    TaskTemplateUpdateSchema,
    TaskTemplatePreviewRequestSchema,
    TaskTemplateListQuerySchema,
    TaskTemplateBulkSaveSchema,
    TaskTemplateBulkSaveResponseSchema,
    TemplatePathParamsSchema,
} from "../schemas/task-template.schema";
import {
    TaskTemplateSchema,
    TaskTemplatePreviewSchema,
} from "../types/taskTemplate";

export { TaskTemplateAssigneeRole } from "../types/taskTemplate";

export {
    TaskTemplateCreateSchema,
    TaskTemplateUpdateSchema,
    TaskTemplatePreviewRequestSchema,
    TaskTemplateBulkSaveSchema,
};

export type {
    TaskTemplateCreate,
    TaskTemplateUpdate,
    TaskTemplatePreviewRequest,
    TaskTemplateBulkSave,
} from "../schemas/task-template.schema";

const c = initContract();

export const taskTemplateContract = c.router(
    {
        listTaskTemplates: {
            method: "GET",
            path: "/organizations/:organizationId",
            pathParams: OrganizationPathParamsSchema,
            query: TaskTemplateListQuerySchema,
            responses: {
                200: z.object({
                    taskTemplates: z.array(TaskTemplateSchema),
                    hasMore: z.boolean(),
                }),
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        getTaskTemplate: {
            method: "GET",
            path: "/organizations/:organizationId/:templateId",
            pathParams: OrganizationPathParamsSchema.merge(
                TemplatePathParamsSchema,
            ),
            responses: {
                200: TaskTemplateSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        createTaskTemplate: {
            method: "POST",
            path: "/organizations/:organizationId",
            pathParams: OrganizationPathParamsSchema,
            body: TaskTemplateCreateSchema,
            responses: {
                201: TaskTemplateSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        updateTaskTemplate: {
            method: "PATCH",
            path: "/organizations/:organizationId/:templateId",
            pathParams: OrganizationPathParamsSchema.merge(
                TemplatePathParamsSchema,
            ),
            body: TaskTemplateUpdateSchema,
            responses: {
                200: TaskTemplateSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        deleteTaskTemplate: {
            method: "DELETE",
            path: "/organizations/:organizationId/:templateId",
            pathParams: OrganizationPathParamsSchema.merge(
                TemplatePathParamsSchema,
            ),
            responses: {
                200: c.noBody(),
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        bulkSaveTaskTemplates: {
            method: "POST",
            path: "/organizations/:organizationId/bulk-save",
            pathParams: OrganizationPathParamsSchema,
            body: TaskTemplateBulkSaveSchema,
            responses: {
                200: TaskTemplateBulkSaveResponseSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        previewTaskTemplate: {
            method: "POST",
            path: "/organizations/:organizationId/workspaces/:workspaceId/:templateId/preview",
            pathParams: OrganizationPathParamsSchema.merge(
                WorkspacePathParamsSchema,
            ).merge(TemplatePathParamsSchema),
            body: TaskTemplatePreviewRequestSchema,
            responses: {
                200: TaskTemplatePreviewSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
    },
    {
        pathPrefix: "/task-templates",
    },
);
